import { blockComponent, blocksOn, formPages, layoutOf } from '@ayumia/component-runtime';
import { text } from '../../../app/i18n';
import { blockRegion, sectionRegion } from '../../../work/FormLayoutParts';
import type { InsertResult, LibraryEntry } from '../../editor/canvas-types';
import { getAt, insertAt, moveAcross, setAt } from '../../editor/json-pointer';
import { SECTION_KINDS } from '../forms-model';
import { addPage, blocksOf, type Block, declaredPages, moveSectionToPage, movePageTo, placeBlock, removePage, setBlockProp, setPlacement, updateBlock, updatePage, withoutBlock, type Template } from '../layout-model';
import { blockOf, componentByName, currentLayout, fr, refusalOf, type EditorRegistry } from '../layout-registry';

/**
 * Points d'arrivée du profil « formulaire » (ADR-146 §5.3, addendum (13)) : une seule grammaire de cibles
 * sert l'insertion par la bibliothèque (clic, « Ajouter ici »), le dépôt d'un glisser depuis la
 * bibliothèque, le déplacement d'un élément sur le canevas ou dans les calques, et le parcours au clavier.
 * Pures : chaque opération rend un nouveau gabarit, contrôlé par la recette ; rien ne touche le DOM.
 *
 * Cibles (jamais montrées) :
 * - `before:block:<id>`, `after:block:<id>`, `region:<région>[@<page>]` ;
 * - `before:section:<id>`, `after:section:<id>`, `end:sections` ;
 * - `before:field:<chemin>`, `after:field:<chemin>`, `section:<id>` (à la fin de la partie) ;
 * - `before:page:<id>`, `end:pages`.
 */

type Section = Template['sections'][number];
type Entry = Section['fields'][number];
type Localized = Record<string, string>;

export type DragKind = 'block' | 'section' | 'field' | 'page';
export type DragSource<D = Template> = { kind: 'library'; entry: LibraryEntry<D> } | { kind: 'node'; id: string };
/** Résultat d'un dépôt : le document et l'élément à choisir, un refus avec son motif, ou `null` (rien ne change). */
export type DropResult = InsertResult<Template> | null;

const PLAIN_REFUSAL = 'Un champ d’une liste de lignes ou d’une partie répétée reste dans sa partie.';
const LAST_FIELD_REFUSAL = 'Une partie garde au moins un champ : ajoutez-en un autre avant de déplacer celui-ci.';
const LAST_SECTION_REFUSAL = 'Une page garde au moins une partie : déplacez d’abord une autre partie, ou supprimez la page.';

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);
const sectionIndex = (template: Template, id: string): number => template.sections.findIndex((section) => section.section_id === id);
const grouped = (section: Section | undefined): boolean => section !== undefined && (section.repeat !== undefined || section.series !== undefined);

/** Sorte d'un élément glissé : bloc, partie, champ ou page. */
export function kindOf(source: DragSource): DragKind | null {
  const id = source.kind === 'library' ? source.entry.id : source.id;
  if (id.startsWith('block:')) return 'block';
  if (id.startsWith('section:')) return 'section';
  if (id.startsWith('field:')) return 'field';
  if (id.startsWith('page:')) return 'page';
  return null;
}

/** `region:main@pieces` → région et page (la page n'a de sens que si le gabarit déclare des pages). */
export function regionTarget(target: string): { region: string; page?: string } {
  const [region, page] = target.slice(7).split('@');
  return { region: region ?? '', ...(page === undefined || page === '' ? {} : { page }) };
}

const pagesDeclared = (template: Template): boolean => declaredPages(template).length > 0;

/** Pages qui montrent une partie, si le gabarit déclare des pages. */
function pagesOfSection(template: Template, sectionId: string): string[] | undefined {
  return pagesDeclared(template) ? formPages(template).filter((page) => page.sections.includes(sectionId)).map((page) => page.page_id) : undefined;
}

/** Partie d'un chemin de champ (`/sections/2/fields/0`). */
const sectionOfPointer = (template: Template, pointer: string): Section | undefined => template.sections[Number(pointer.split('/')[2])];

// ---- Blocs.

export interface BlockSpot {
  region: string;
  index?: number;
  page_ids?: string[];
}

/** Région et position d'un bloc inséré au point donné (sinon la première région qui accepte sa catégorie). */
export function blockSpot(template: Template, registry: EditorRegistry, target: string | null, category: string): BlockSpot | null {
  const body = currentLayout(registry, template)?.body;
  const ofSection = (sectionId: string): BlockSpot | null => {
    const region = sectionRegion(body, layoutOf(template)?.placements?.[sectionId]?.region);
    const pages = pagesOfSection(template, sectionId);
    return region === undefined ? null : { region, ...(pages === undefined ? {} : { page_ids: pages }) };
  };
  if (target?.startsWith('region:') === true) {
    const { region, page } = regionTarget(target);
    return { region, ...(page !== undefined && pagesDeclared(template) ? { page_ids: [page] } : {}) };
  }
  const relative = /^(before|after):block:(.+)$/.exec(target ?? '');
  if (relative !== null) {
    const blocks = blocksOf(template);
    const index = blocks.findIndex((block) => block.block_id === relative[2]);
    const block = blocks[index];
    if (block !== undefined) return { region: block.region, index: relative[1] === 'after' ? index + 1 : index, ...(block.page_ids === undefined ? {} : { page_ids: [...block.page_ids] }) };
  }
  const sectionId = target?.startsWith('after:section:') === true ? target.slice(14) : target?.startsWith('before:section:') === true ? target.slice(15) : target?.startsWith('section:') === true ? target.slice(8) : /^(after|before):field:/.test(target ?? '') ? sectionOfPointer(template, target!.replace(/^(after|before):field:/, ''))?.section_id : undefined;
  if (sectionId !== undefined) {
    const spot = ofSection(sectionId);
    if (spot !== null) return spot;
  }
  const regions = body?.regions ?? [];
  const region = regions.find((candidate) => candidate.accepts.includes(category as never)) ?? regions[0];
  return region === undefined ? null : { region: region.region_id };
}

const categoryOf = (registry: EditorRegistry, componentRef: string): string => blockOf(componentByName(registry, componentRef))?.category ?? 'display';

function moveBlockTo(template: Template, registry: EditorRegistry, blockId: string, target: string): DropResult {
  const block = blocksOf(template).find((candidate) => candidate.block_id === blockId);
  if (block === undefined || target === `before:block:${blockId}` || target === `after:block:${blockId}`) return null;
  const without = withoutBlock(template, blockId);
  const spot = blockSpot(without, registry, target, categoryOf(registry, blockComponent(block).name));
  if (spot === null) return null;
  // Une région sans page garde les pages du bloc ; une région d'une page l'y met ; à côté d'un bloc, celles du voisin.
  const keepPages = target.startsWith('region:') && regionTarget(target).page === undefined;
  // La position est calculée sans le bloc ; `placeBlock` le retire puis l'insère à cette position.
  const next = placeBlock(template, blockId, { region: spot.region, ...(spot.index === undefined ? {} : { index: spot.index }), page_ids: keepPages ? block.page_ids : spot.page_ids });
  if (same(next, template)) return null;
  const refused = refusalOf(registry, template, next);
  return refused === null ? { document: next, id: `block:${blockId}` } : { refused };
}

// ---- Parties.

export interface SectionSpot {
  /** Position dans la liste des parties (fin si absente). */
  index?: number;
  /** Région déclarée à poser ; `null` : la région par défaut (aucun réglage) ; absente : inchangée. */
  region?: string | null;
  page?: string;
}

/** Région déclarée d'une partie (`undefined` : celle par défaut du gabarit). */
const declaredRegion = (template: Template, sectionId: string): string | undefined => layoutOf(template)?.placements?.[sectionId]?.region;

/** Place d'une partie insérée ou déplacée au point donné : elle rejoint la page et la région de sa voisine. */
export function sectionSpot(template: Template, registry: EditorRegistry, target: string | null): SectionSpot {
  const relative = /^(before|after):section:(.+)$/.exec(target ?? '') ?? (/^after:field:/.test(target ?? '') ? ['', 'after', sectionOfPointer(template, target!.slice(12))?.section_id ?? ''] : null);
  if (relative !== null) {
    const index = sectionIndex(template, relative[2] ?? '');
    if (index >= 0) {
      const neighbour = template.sections[index]!.section_id;
      const page = pagesOfSection(template, neighbour)?.[0];
      return { index: relative[1] === 'after' ? index + 1 : index, region: declaredRegion(template, neighbour) ?? null, ...(page === undefined ? {} : { page }) };
    }
  }
  if (target?.startsWith('region:') === true) {
    const { region, page } = regionTarget(target);
    const body = currentLayout(registry, template)?.body;
    // À la fin de la région sur cette page : après la dernière partie qui s'y montre.
    const shown = pagesDeclared(template) && page !== undefined ? (formPages(template).find((candidate) => candidate.page_id === page)?.sections ?? []) : template.sections.map((section) => section.section_id);
    let last = -1;
    template.sections.forEach((section, index) => {
      if (shown.includes(section.section_id) && sectionRegion(body, declaredRegion(template, section.section_id)) === region) last = index;
    });
    return { ...(last >= 0 ? { index: last + 1 } : {}), region, ...(page !== undefined && pagesDeclared(template) ? { page } : {}) };
  }
  return {};
}

/** Pose la région et la page d'une partie selon sa place. */
function settle(template: Template, sectionId: string, spot: SectionSpot): Template {
  let next = template;
  if (spot.region !== undefined && layoutOf(next) !== undefined) next = setPlacement(next, sectionId, { region: spot.region ?? undefined });
  if (spot.page !== undefined && pagesDeclared(next)) {
    const current = pagesOfSection(next, sectionId) ?? [];
    const cited = declaredPages(next).some((page) => page.sections.includes(sectionId));
    if (!cited || !current.includes(spot.page)) next = moveSectionToPage(next, sectionId, spot.page);
  }
  return next;
}

/** Insère une partie neuve au point donné (bibliothèque, dépôt). */
export function insertSection(template: Template, registry: EditorRegistry, created: Section, target: string | null): InsertResult<Template> {
  const spot = sectionSpot(template, registry, target);
  const next = settle(insertAt(template, '/sections', created, spot.index), created.section_id, spot);
  const refused = refusalOf(registry, template, next);
  return refused === null ? { document: next, id: `section:${created.section_id}` } : { refused };
}

function moveSectionTo(template: Template, registry: EditorRegistry, sectionId: string, target: string): DropResult {
  const from = sectionIndex(template, sectionId);
  if (from < 0 || target === `before:section:${sectionId}` || target === `after:section:${sectionId}`) return null;
  const section = template.sections[from]!;
  const rest = template.sections.filter((candidate) => candidate !== section);
  const without = { ...template, sections: rest } as Template;
  const spot = target === 'end:sections' ? {} : sectionSpot(without, registry, target);
  const at = spot.index ?? rest.length;
  let next = { ...template, sections: [...rest.slice(0, at), section, ...rest.slice(at)] } as Template;
  // Une page ne se vide pas : la partie seule de sa page n'en sort pas.
  if (spot.page !== undefined && pagesDeclared(template)) {
    const home = declaredPages(template).find((page) => page.sections.includes(sectionId));
    if (home !== undefined && home.page_id !== spot.page && home.sections.length === 1) return { refused: LAST_SECTION_REFUSAL };
  }
  next = settle(next, sectionId, spot);
  if (same(next, template)) return null;
  const refused = refusalOf(registry, template, next);
  return refused === null ? { document: next, id: `section:${sectionId}` } : { refused };
}

// ---- Champs.

/** Liste et position d'un champ inséré au point donné ; `null` : aucune partie ne peut le recevoir. */
export function fieldSpot(template: Template, target: string | null): { list: string; index: number } | null {
  let s = -1;
  let at: number | undefined;
  let list: string | undefined;
  if (target?.startsWith('section:') === true) s = sectionIndex(template, target.slice(8));
  else if (target?.startsWith('after:section:') === true) s = sectionIndex(template, target.slice(14));
  else if (target?.startsWith('before:section:') === true) s = sectionIndex(template, target.slice(15));
  else if (/^(after|before):field:/.test(target ?? '')) {
    const after = target!.startsWith('after:');
    const pointer = target!.slice(after ? 12 : 13);
    const segments = pointer.split('/');
    s = Number(segments[2]);
    const index = Number(segments[segments.length - 1]);
    list = segments.slice(0, -1).join('/');
    at = after ? index + 1 : index;
  }
  if (s < 0) s = template.sections.findIndex((section) => !grouped(section));
  if (s < 0 || template.sections[s] === undefined) return null;
  const pointer = list ?? `/sections/${s}/fields`;
  const size = ((getAt(template, pointer) as unknown[] | undefined) ?? []).length;
  return { list: pointer, index: Math.min(at ?? size, size) };
}

function moveFieldTo(template: Template, pointer: string, target: string): DropResult {
  const segments = pointer.split('/');
  const fromList = segments.slice(0, -1).join('/');
  const fromIndex = Number(segments[segments.length - 1]);
  const entries = (getAt(template, fromList) as Entry[] | undefined) ?? [];
  if (entries[fromIndex] === undefined) return null;
  const spot = fieldSpot(template, target);
  if (spot === null) return null;
  if (spot.list === fromList) {
    const to = spot.index > fromIndex ? spot.index - 1 : spot.index;
    if (to === fromIndex) return null;
    return { document: moveAcross(template, { list: fromList, index: fromIndex }, { list: fromList, index: to }), id: `field:${fromList}/${to}` };
  }
  const source = sectionOfPointer(template, pointer);
  const destination = sectionOfPointer(template, spot.list);
  if (grouped(source) || grouped(destination) || fromList.includes('/repeat/') || spot.list.includes('/repeat/')) return { refused: PLAIN_REFUSAL };
  if (entries.length <= 1) return { refused: LAST_FIELD_REFUSAL };
  return { document: moveAcross(template, { list: fromList, index: fromIndex }, spot), id: `field:${spot.list}/${spot.index}` };
}

// ---- Pages.

function movePage(template: Template, pageId: string, target: string): DropResult {
  const pages = declaredPages(template);
  if (!pages.some((page) => page.page_id === pageId) || target === `before:page:${pageId}`) return null;
  const rest = pages.filter((page) => page.page_id !== pageId);
  const at = target === 'end:pages' ? rest.length : rest.findIndex((page) => `before:page:${page.page_id}` === target);
  if (at < 0) return null;
  const next = movePageTo(template, pageId, at);
  return same(next, template) ? null : { document: next, id: `page:${pageId}` };
}

/** Déplace un élément existant (bloc, partie, champ, page) vers une cible ; recette contrôlée. */
export function moveNode(template: Template, registry: EditorRegistry, id: string, target: string): DropResult {
  if (id.startsWith('block:')) return moveBlockTo(template, registry, id.slice(6), target);
  if (id.startsWith('section:')) return moveSectionTo(template, registry, id.slice(8), target);
  if (id.startsWith('field:')) return moveFieldTo(template, id.slice(6), target);
  if (id.startsWith('page:')) return movePage(template, id.slice(5), target);
  return null;
}

/** Ce qu'un dépôt ferait : insertion de la bibliothèque (même calcul que le clic) ou déplacement. */
export function performDrop(template: Template, registry: EditorRegistry, source: DragSource, target: string): DropResult {
  if (source.kind === 'library') return source.entry.insert(template, target);
  return moveNode(template, registry, source.id, target);
}

/** La cible convient-elle à la sorte glissée ? (une ligne d'insertion n'est proposée qu'à bon escient) */
export function targetFits(kind: DragKind, target: string): boolean {
  if (kind === 'block') return /^(before|after):block:|^region:|^section:/.test(target);
  if (kind === 'section') return /^(before|after):section:|^region:|^end:sections$/.test(target);
  if (kind === 'field') return /^(before|after):field:|^section:/.test(target);
  return /^before:page:|^end:pages$/.test(target);
}

// ---- Places au clavier (et annonces) : l'ordre du document, en mots.

export interface DropSlot {
  target: string;
  /** Place en mots (« Page « Fiche » · Contenu · avant « Arrivée » »). */
  label: string;
  /** Page à montrer pendant le choix. */
  page?: string;
}

const quote = (value: string): string => `« ${value} »`;
const sectionLabel = (section: Section): string => text(section.label, 'fr', 'Partie sans nom');
const fieldLabel = (entry: Entry): string => text(entry.field.label, 'fr', 'Champ sans nom');

/** Libellé d'un bloc tel que les calques le montrent. */
export function blockLabel(registry: EditorRegistry, block: Block): string {
  return fr(block.label, fr(componentByName(registry, blockComponent(block).name)?.manifest.component.label, 'Bloc'));
}

/**
 * Places possibles pour un élément glissé, dans l'ordre du document ; celles qui ne changent rien sont
 * retirées. `start` est la place de l'élément lui-même (pour commencer le parcours à côté de lui).
 */
export function dropSlots(template: Template, registry: EditorRegistry, source: DragSource): { slots: DropSlot[]; start: number } {
  const kind = kindOf(source);
  const all: DropSlot[] = [];
  const body = currentLayout(registry, template)?.body;
  const declared = pagesDeclared(template);
  const pages = formPages(template);
  const pagePrefix = (pageId: string): string => (declared ? `Page ${quote(fr(pages.find((page) => page.page_id === pageId)?.label, 'Page'))} · ` : '');
  if (kind === 'block' && body !== undefined) {
    for (const page of pages) {
      for (const region of body.regions.filter((candidate) => candidate.role !== 'footer')) {
        const where = `${pagePrefix(page.page_id)}${fr(region.label, 'Zone')}`;
        const blocks = blocksOn(template, page.page_id).filter((block) => (block.page_ids !== undefined || !declared) && blockRegion(body, block.region) === region.region_id);
        for (const block of blocks) all.push({ target: `before:block:${block.block_id}`, label: `${where} · avant ${quote(blockLabel(registry, block))}`, page: page.page_id });
        all.push({ target: `region:${region.region_id}${declared ? `@${page.page_id}` : ''}`, label: `${where} · à la fin`, page: page.page_id });
      }
    }
    if (declared) {
      const persistent = blocksOf(template).filter((block) => block.page_ids === undefined);
      for (const block of persistent) all.push({ target: `before:block:${block.block_id}`, label: `Sur toutes les pages · avant ${quote(blockLabel(registry, block))}` });
      const last = persistent[persistent.length - 1];
      if (last !== undefined) all.push({ target: `after:block:${last.block_id}`, label: `Sur toutes les pages · à la fin` });
    }
  } else if (kind === 'section') {
    if (body === undefined || layoutOf(template) === undefined) {
      for (const section of template.sections) all.push({ target: `before:section:${section.section_id}`, label: `Avant ${quote(sectionLabel(section))}` });
      all.push({ target: 'end:sections', label: 'À la fin du formulaire' });
    } else {
      for (const page of pages) {
        for (const region of body.regions.filter((candidate) => candidate.accepts.includes('section'))) {
          const where = `${pagePrefix(page.page_id)}${fr(region.label, 'Zone')}`;
          for (const section of template.sections.filter((candidate) => page.sections.includes(candidate.section_id) && sectionRegion(body, declaredRegion(template, candidate.section_id)) === region.region_id)) {
            all.push({ target: `before:section:${section.section_id}`, label: `${where} · avant ${quote(sectionLabel(section))}`, page: page.page_id });
          }
          all.push({ target: `region:${region.region_id}${declared ? `@${page.page_id}` : ''}`, label: `${where} · à la fin`, page: page.page_id });
        }
      }
    }
  } else if (kind === 'field') {
    const own = source.kind === 'node' ? sectionOfPointer(template, source.id.slice(6)) : undefined;
    template.sections.forEach((section, s) => {
      if (own !== undefined && grouped(own) ? section !== own : grouped(section)) return;
      const page = declared ? pagesOfSection(template, section.section_id)?.[0] : undefined;
      section.fields.forEach((entry, f) => all.push({ target: `before:field:/sections/${s}/fields/${f}`, label: `Partie ${quote(sectionLabel(section))} · avant ${quote(fieldLabel(entry))}`, ...(page === undefined ? {} : { page }) }));
      all.push({ target: `section:${section.section_id}`, label: `Partie ${quote(sectionLabel(section))} · à la fin`, ...(page === undefined ? {} : { page }) });
    });
  } else if (kind === 'page') {
    for (const page of declaredPages(template)) all.push({ target: `before:page:${page.page_id}`, label: `Avant la page ${quote(fr(page.label, 'Page'))}`, page: page.page_id });
    all.push({ target: 'end:pages', label: 'Après la dernière page' });
  }
  if (source.kind === 'library') return { slots: all, start: 0 };
  // Les places qui ne changent rien marquent la place actuelle de l'élément.
  const slots: DropSlot[] = [];
  let start = -1;
  for (const slot of all) {
    if (moveNode(template, registry, source.id, slot.target) === null) {
      if (start < 0) start = slots.length;
      continue;
    }
    slots.push(slot);
  }
  return { slots, start: start < 0 ? 0 : start };
}

// ---- Libellés modifiés sur place (double-clic).

/** Partie du libellé d'un bloc montrée par le canevas : l'intitulé du bandeau, sinon le libellé du bloc. */
export type LabelPart = 'label' | 'eyebrow';

/** Texte français actuel d'un libellé (champ, partie, page, bloc). */
export function labelText(template: Template, id: string, part: LabelPart = 'label'): string {
  if (id.startsWith('field:')) {
    const entry = getAt(template, id.slice(6)) as Entry | undefined;
    return entry === undefined ? '' : text(entry.field.label, 'fr', '');
  }
  if (id.startsWith('section:')) {
    const section = template.sections[sectionIndex(template, id.slice(8))];
    return section === undefined ? '' : text(section.label, 'fr', '');
  }
  if (id.startsWith('page:')) return fr(declaredPages(template).find((page) => page.page_id === id.slice(5))?.label, '');
  if (id.startsWith('block:')) {
    const block = blocksOf(template).find((candidate) => candidate.block_id === id.slice(6));
    const eyebrow = (block?.props as Record<string, unknown> | undefined)?.['eyebrow'];
    if (part === 'eyebrow' && eyebrow !== null && typeof eyebrow === 'object') return fr(eyebrow, '');
    return fr(block?.label, '');
  }
  if (id === 'form') return text(template.label, 'fr', '');
  return '';
}

const withFrench = (label: unknown, value: string): Localized => ({ ...((label ?? {}) as Localized), fr: value });

/** Change le libellé français d'un élément ; les autres langues restent. Un libellé vide est refusé. */
export function renameLabel(template: Template, id: string, value: string, part: LabelPart = 'label'): Template | { refused: string } {
  const clean = value.replace(/\s+/g, ' ').trim();
  if (clean === '') return { refused: 'Un libellé ne peut pas être vide.' };
  if (clean === labelText(template, id, part)) return template;
  if (id.startsWith('field:')) {
    const pointer = `${id.slice(6)}/field/label`;
    return getAt(template, id.slice(6)) === undefined ? template : setAt(template, pointer, withFrench(getAt(template, pointer), clean));
  }
  if (id.startsWith('section:')) {
    const s = sectionIndex(template, id.slice(8));
    return s < 0 ? template : setAt(template, `/sections/${s}/label`, withFrench(template.sections[s]!.label, clean));
  }
  if (id.startsWith('page:')) {
    const page = declaredPages(template).find((candidate) => candidate.page_id === id.slice(5));
    return page === undefined ? template : updatePage(template, page.page_id, { label: withFrench(page.label, clean) as typeof page.label });
  }
  if (id.startsWith('block:')) {
    const blockId = id.slice(6);
    const block = blocksOf(template).find((candidate) => candidate.block_id === blockId);
    if (block === undefined) return template;
    const eyebrow = (block.props as Record<string, unknown> | undefined)?.['eyebrow'];
    const hasEyebrow = eyebrow !== null && typeof eyebrow === 'object';
    // L'intitulé du bandeau et le libellé du bloc vont ensemble tant qu'ils disaient la même chose.
    const together = hasEyebrow && fr(eyebrow, '') === fr(block.label, '');
    let next = template;
    if (hasEyebrow && (part === 'eyebrow' || together)) next = setBlockProp(next, blockId, 'eyebrow', withFrench(eyebrow, clean));
    if (part === 'label' || together || !hasEyebrow) next = updateBlock(next, blockId, { label: withFrench(block.label, clean) as typeof block.label });
    return next;
  }
  if (id === 'form') return { ...template, label: withFrench(template.label, clean) } as Template;
  return template;
}

// ---- Pages depuis la barre d'onglets du canevas.

/**
 * Ajoute une page « Nouvelle page » avec une partie neuve (une page n'est jamais vide), placée dans la
 * région des parties. Sans pages déclarées, les parties actuelles forment d'abord la page 1.
 */
export function addBlankPage(template: Template, registry: EditorRegistry): InsertResult<Template> {
  if (layoutOf(template) === undefined) return { refused: 'Choisissez d’abord une mise en page dans « Pages ».' };
  let next = template;
  if (!pagesDeclared(next)) next = addPage(next, { fr: 'Page 1' }, next.sections.map((section) => section.section_id)).template;
  const created = SECTION_KINDS[0]!.create(next) as Section;
  next = insertAt(next, '/sections', created);
  const used = new Set(declaredPages(next).map((page) => fr(page.label, '')));
  let index = declaredPages(next).length + 1;
  while (used.has(`Page ${index}`)) index += 1;
  const page = addPage(next, { fr: `Page ${index}` }, [created.section_id]);
  const refused = refusalOf(registry, template, page.template);
  return refused === null ? { document: page.template, id: `page:${page.id}` } : { refused };
}

/** Supprime une page depuis le canevas : ses parties rejoignent la page voisine (rien n'est perdu). */
export function deletePage(template: Template, pageId: string): Template {
  return removePage(template, pageId);
}
